import { LayoutDashboard, ListChecks, Users } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import { Tooltip } from 'common-src/components/base';
import { getCurrentUserId, getIsAdmin } from 'common-src/features/auth';
import { singleModelSelector } from 'common-src/utils/selectorUtils';

import { CLIENT_ROOT } from 'src/constants/paths';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useTabs from 'src/hooks/useTabs';

import CapacityChip from '../CapacityChip';
import OrgSwitcher from '../OrgSwitcher';
import TaskNotifications from '../TaskNotifications';
import styles from './CareOrchestrationHeader.module.scss';

const CareOrchestrationHeader = ({
  view,
  ownerFilter,
  readinessStats,
  stats,
  leadTaskCount,
  tabAvailability,
  onChangeView,
  onClearSelections,
  taskNotificationPreferences,
  isTaskNotificationPreferencesLoading,
  isTaskNotificationPreferencesSaving,
  onPatchTaskNotificationPreferences,
  onSearchTaskNotificationSlackUsers,
  onSendTaskNotificationTestMessage,
  taskNotifications,
  isTaskNotificationsLoading,
  onDismissTaskNotification,
  onOpenTaskNotification,
  onMarkAllTaskNotificationsRead,
  browserNotificationPermission,
  onRequestBrowserNotificationPermission,
  organizationScope,
  defaultOrgScope,
  onChangeOrganizationScope,
  onSetDefaultOrganizationScope,
  isSettingDefaultOrganizationScope,
}) => {
  const { onSelectTab } = useTabs();
  const safeReadiness = readinessStats ?? { red: 0 };
  const safeStats = stats ?? { total: 0 };
  const safeTabAvailability = tabAvailability ?? {};
  const currentUser = useCustomSelector((state) =>
    singleModelSelector(state, 'Client', getCurrentUserId(state)),
  );
  const isAdmin = useCustomSelector(getIsAdmin);
  const today = new Date().toISOString().slice(0, 10);
  const userInitials = `${currentUser?.firstName?.charAt(0) || ''}${
    currentUser?.lastName?.charAt(0) || ''
  }`;

  const tabs = [
    {
      key: 'readiness',
      label: 'Prepare',
      badge: safeReadiness.red > 0 ? `${safeReadiness.red} not ready` : null,
      disabled: !safeTabAvailability.readiness,
      tooltipText: !safeTabAvailability.readiness ? 'Coming soon' : null,
    },
    {
      key: 'tasks',
      label: 'Jobs to be done',
      count: safeStats.total,
      disabled: !safeTabAvailability.tasks,
      tooltipText: !safeTabAvailability.tasks ? 'Coming soon' : null,
    },
    {
      key: 'leads',
      label: 'Leads',
      count: leadTaskCount,
      icon: Users,
      disabled: !safeTabAvailability.leads,
      tooltipText: !safeTabAvailability.leads ? 'Coming soon' : null,
    },
    {
      key: 'action-area',
      label: 'Action Area',
      disabled: !safeTabAvailability['action-area'],
      tooltipText: !safeTabAvailability['action-area'] ? 'Coming soon' : null,
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.tabs}>
          {tabs.map((item) => {
            const tabButton = (
              <button
                key={item.key}
                type="button"
                disabled={item.disabled}
                aria-disabled={item.disabled}
                onClick={() => {
                  if (item.disabled) return;
                  onChangeView(item.key);
                  onClearSelections();
                }}
                className={`${styles.tabButton} ${view === item.key ? styles.activeTab : ''} ${
                  item.disabled ? styles.disabledTab : ''
                }`}
              >
                {item.icon ? (
                  <item.icon
                    size={13}
                    strokeWidth={1.9}
                    className={styles.tabIcon}
                    aria-hidden="true"
                  />
                ) : null}
                {item.label}
                {item.count != null && <span className={styles.tabCount}>{item.count}</span>}
                {item.badge && <span className={styles.tabBadge}>{item.badge}</span>}
              </button>
            );

            if (!item.tooltipText) return tabButton;

            return (
              <Tooltip key={item.key} title={item.tooltipText}>
                <span className={styles.tooltipWrapper}>{tabButton}</span>
              </Tooltip>
            );
          })}
        </div>
        <div className={styles.meta}>
          <span className={styles.today}>{today}</span>
          {view === 'tasks' && ownerFilter === 'my' && <CapacityChip />}
          <TaskNotifications
            preferences={taskNotificationPreferences}
            currentUserEmail={currentUser?.email || ''}
            isLoading={isTaskNotificationPreferencesLoading}
            isSaving={isTaskNotificationPreferencesSaving}
            onPatchPreferences={onPatchTaskNotificationPreferences}
            onSearchSlackUsers={onSearchTaskNotificationSlackUsers}
            onSendTestMessage={onSendTaskNotificationTestMessage}
            taskNotifications={taskNotifications}
            isTaskNotificationsLoading={isTaskNotificationsLoading}
            onDismissTaskNotification={onDismissTaskNotification}
            onOpenTaskNotification={onOpenTaskNotification}
            onMarkAllTaskNotificationsRead={onMarkAllTaskNotificationsRead}
            browserNotificationPermission={browserNotificationPermission}
            onRequestBrowserNotificationPermission={onRequestBrowserNotificationPermission}
          />
          {safeTabAvailability['bulk-assign'] && (
            <button
              type="button"
              className={styles.adminDashboardButton}
              aria-pressed={view === 'bulk-assign'}
              onClick={() => {
                onChangeView('bulk-assign');
                onClearSelections();
              }}
              aria-label="Bulk assign"
              title="Bulk assign"
            >
              <ListChecks
                size={16}
                strokeWidth={1.9}
                className={styles.adminDashboardIcon}
                aria-hidden="true"
              />
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className={styles.adminDashboardButton}
              onClick={() => onSelectTab({ path: `${CLIENT_ROOT}/task-titles` })}
              aria-label="Open Task Title Manager"
              title="Task Title Manager"
            >
              <LayoutDashboard
                size={16}
                strokeWidth={1.9}
                className={styles.adminDashboardIcon}
                aria-hidden="true"
              />
            </button>
          )}
          <OrgSwitcher
            value={organizationScope}
            defaultOrgScope={defaultOrgScope}
            onSelect={onChangeOrganizationScope}
            onSetDefault={onSetDefaultOrganizationScope}
            isSettingDefault={isSettingDefaultOrganizationScope}
          />
          <div className={styles.avatar}>{userInitials || '--'}</div>
        </div>
      </div>
    </div>
  );
};

CareOrchestrationHeader.propTypes = {
  view: PropTypes.string,
  ownerFilter: PropTypes.string,
  readinessStats: PropTypes.shape({
    red: PropTypes.number,
    yellow: PropTypes.number,
    green: PropTypes.number,
  }),
  stats: PropTypes.shape({
    total: PropTypes.number,
  }),
  leadTaskCount: PropTypes.number,
  tabAvailability: PropTypes.shape({
    readiness: PropTypes.bool,
    tasks: PropTypes.bool,
    leads: PropTypes.bool,
    'bulk-assign': PropTypes.bool,
    'action-area': PropTypes.bool,
  }),
  onChangeView: PropTypes.func,
  onClearSelections: PropTypes.func,
  taskNotificationPreferences: PropTypes.shape({
    notificationsEnabled: PropTypes.bool,
    sound: PropTypes.bool,
    channels: PropTypes.object,
    categories: PropTypes.object,
  }),
  isTaskNotificationPreferencesLoading: PropTypes.bool,
  isTaskNotificationPreferencesSaving: PropTypes.bool,
  onPatchTaskNotificationPreferences: PropTypes.func,
  onSearchTaskNotificationSlackUsers: PropTypes.func,
  onSendTaskNotificationTestMessage: PropTypes.func,
  taskNotifications: PropTypes.arrayOf(PropTypes.object),
  isTaskNotificationsLoading: PropTypes.bool,
  onDismissTaskNotification: PropTypes.func,
  onOpenTaskNotification: PropTypes.func,
  onMarkAllTaskNotificationsRead: PropTypes.func,
  browserNotificationPermission: PropTypes.string,
  onRequestBrowserNotificationPermission: PropTypes.func,
  organizationScope: PropTypes.string,
  defaultOrgScope: PropTypes.string,
  onChangeOrganizationScope: PropTypes.func,
  onSetDefaultOrganizationScope: PropTypes.func,
  isSettingDefaultOrganizationScope: PropTypes.bool,
};

export default CareOrchestrationHeader;
